import apiService from './api';
import {
  isMicropayScopedToken,
  readSharedAuthCookieIncludingCheckout,
  setSharedAuthCookie,
} from './cookieAuth';
import { writeLastActivityAt } from '../utils/sessionInactivity';
import { getOrCreateFingerprintHash } from '../utils/fingerprint';
import type { User } from '../types';

/**
 * Mailbox and product-link checkout share one sign-in.
 * A checkout token is promoted to a mailbox session before the app treats the visitor as logged out.
 */
/**
 * Exchanges a micropay-scoped checkout token for a full mailbox session.
 * Returns null when the account needs 2FA first; the visitor then signs in normally.
 */
export async function promoteCheckoutToken(token: string): Promise<{ accessToken: string; refreshToken: string } | null> {
  const response = await fetch('/api/Auth/session-from-checkout', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'X-Fingerprint': getOrCreateFingerprintHash() },
  });
  if (!response.ok) return null;
  const body = await response.json() as { accessToken?: string; refreshToken?: string; requiresMfa?: boolean };
  if (body.requiresMfa || !body.accessToken) return null;
  return { accessToken: body.accessToken, refreshToken: body.refreshToken || '' };
}

export async function adoptSharedSession(): Promise<User | null> {
  const shared = readSharedAuthCookieIncludingCheckout();
  if (!shared?.token || !shared.userId || !shared.username) return null;

  let accessToken = shared.token;
  let refreshToken = '';

  if (isMicropayScopedToken(shared.token)) {
    const promoted = await promoteCheckoutToken(shared.token);
    if (!promoted) return null;
    accessToken = promoted.accessToken;
    refreshToken = promoted.refreshToken;
  }

  const user: User = {
    userId: shared.userId,
    username: shared.username,
    loginHandle: shared.username,
    displayUsername: shared.username,
  };

  writeLastActivityAt();
  localStorage.setItem('user', JSON.stringify(user));
  apiService.setTokens(accessToken, refreshToken);
  setSharedAuthCookie(shared.userId, shared.username, accessToken);
  return user;
}
